import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import { GoogleMap, Marker, Polyline, DirectionsRenderer } from "@react-google-maps/api";
import { GoogleMapsContext } from "../constants/GoogleMapsProvider";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Typography,
    IconButton,
    useMediaQuery,
    useTheme,
    Divider,
    ListItemIcon,
    FormControlLabel,
    Switch,
    Radio,
    RadioGroup,
    FormLabel,
    Stack,
    Chip,
    Autocomplete,
    createFilterOptions,
    Avatar,
    Link
} from "@mui/material";
import { FaFileAlt, FaTrash, FaUpload } from "react-icons/fa";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import "react-datepicker/dist/react-datepicker.css";
import { createActivity, getEvents, updateActivity, getEventsRange, getVehicles, createVehicle, getArtistCrewMembers, deleteActivity, getActivityById, addActivityGroup, removeActivityGroup, addCrewMemberToActivity, removeCrewMemberFromActivity, getCrewMemberGroups, createBooking, createSeat, deleteBooking, deleteSeat, updateBooking, addEventCrew } from "@firebasegen/default-connector";
import { resolvePlace } from "../services/placeService";
import moment from "moment-timezone";
import { ActivityTypes, ActivityStatuses, searchFlight, parseFlightNumbers } from "../constants/common";
import { useEvents, useArtists } from "../contexts/ArtistContext";
import LocationSearchField from "../components/LocationSearchField";
import StopsEditor, { STOPS_ELIGIBLE_TYPES, parseRouteDetails, serializeRouteDetails } from "../components/Activities/StopsEditor";
import { httpsCallable } from "firebase/functions";
import { getMe, app, functions, storage } from "../firebaseConfig";
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from "firebase/storage";
import { Permissions } from "../constants/permissions";
import TmDialog from "../components/TmDialog";
import useRefreshData from "../hooks/useRefreshData";

const ActivityFileList = ({ activityId, currentActivity, setCurrentActivity }) => {
    const [uploading, setUploading] = useState(false);
    const [files, setFiles] = useState([]);
    const [fetching, setFetching] = useState(true);

    const fetchFiles = async () => {
        if (!activityId) {
            setFetching(false);
            return;
        }
        setFetching(true);
        try {
            const listRef = ref(storage, `activities/${activityId}/files/`);
            const res = await listAll(listRef);
            const fileList = await Promise.all(res.items.map(async (itemRef) => {
                const url = await getDownloadURL(itemRef);
                return { name: itemRef.name, url, fullPath: itemRef.fullPath };
            }));
            setFiles(fileList);
        } catch (error) {
            console.error("Error fetching activity files:", error);
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        if (activityId) {
            fetchFiles();
        } else {
            setFiles([]);
            setFetching(false);
        }
    }, [activityId]);

    const handleFileUpload = async (event) => {
        const fileList = event.target.files;
        if (!fileList || fileList.length === 0 || !activityId) return;

        setUploading(true);
        try {
            for (let i = 0; i < fileList.length; i++) {
                const file = fileList[i];
                const storageRef = ref(storage, `activities/${activityId}/files/${file.name}`);
                await uploadBytes(storageRef, file);
            }
            await fetchFiles();

            // Mark activity as having files
            if (!currentActivity.hasFiles) {
                await updateActivity({ id: activityId, hasFiles: true });
                setCurrentActivity(prev => ({ ...prev, hasFiles: true }));
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            alert("Failed to upload file.");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (fullPath) => {
        if (!window.confirm("Are you sure you want to delete this file?")) return;
        try {
            const fileRef = ref(storage, fullPath);
            await deleteObject(fileRef);
            await fetchFiles();

            // If no files left, update hasFiles to false
            if (files.length <= 1) { // We are deleting the last one
                await updateActivity({ id: activityId, hasFiles: false });
                setCurrentActivity(prev => ({ ...prev, hasFiles: false }));
            }
        } catch (error) {
            console.error("Error deleting file:", error);
            alert("Failed to delete file.");
        }
    };

    if (!activityId) {
        return (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85rem' }}>
                    Save this activity first to upload files.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Files & Attachments
                </Typography>
                <Button
                    component="label"
                    size="small"
                    variant="outlined"
                    startIcon={uploading ? <CircularProgress size={10} color="inherit" /> : <FaUpload size={10} />}
                    disabled={uploading}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1.5, borderRadius: 2 }}
                >
                    {uploading ? 'Uploading...' : 'Add File'}
                    <input type="file" multiple hidden onChange={handleFileUpload} />
                </Button>
            </Box>

            {fetching ? (
                <CircularProgress size={20} />
            ) : files.length > 0 ? (
                <Stack spacing={1}>
                    {files.map(file => (
                        <Box key={file.fullPath} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(0,0,0,0.02)', p: 1, px: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'rgba(0,0,0,0.05)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
                                <FaFileAlt size={16} color="#8b949e" />
                                <Link href={file.url} target="_blank" underline="hover" sx={{ fontSize: '0.85rem', color: 'primary.main', fontWeight: 500, noWrap: true, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {file.name}
                                </Link>
                            </Box>
                            <IconButton size="small" color="error" onClick={() => handleDelete(file.fullPath)} sx={{ opacity: 0.5, '&:hover': { opacity: 1, bgcolor: 'rgba(211, 47, 47, 0.1)' }, p: 0.5 }}>
                                <FaTrash size={12} />
                            </IconButton>
                        </Box>
                    ))}
                </Stack>
            ) : (
                <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.8rem' }}>
                    No files attached.
                </Typography>
            )}
        </Box>
    );
};

const defaultCrewFilter = createFilterOptions();

const ActivityModal = ({ show, handleClose, activity, selectedDate, event, addToReturn, existingActivities = [] }) => {
    const { isLoaded } = useContext(GoogleMapsContext);
    const { refreshData } = useRefreshData();
    // Smart timezone detection with fallback order
    const getSmartTimezone = (eventData, existingActivities = []) => {
        // 1. Try previous activity timezone from the same event
        if (existingActivities && existingActivities.length > 0) {
            // Get the most recent activity with a timezone
            const recentActivity = [...existingActivities]
                .filter(a => a.startTimeZone)
                .sort((a, b) => new Date(b.startTimeTs) - new Date(a.startTimeTs))[0];

            if (recentActivity?.startTimeZone) {
                return recentActivity.startTimeZone;
            }
        }

        // 2. Try event timezone
        if (eventData?.timezone || eventData?.timeZone) {
            return eventData.timezone || eventData.timeZone;
        }

        // 3. Fall back to user timezone
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    };

    const createEmptyActivity = (date, eventData, existingActivities = []) => {
        const startDate = new Date(date || new Date());
        const smartTimezone = getSmartTimezone(eventData, existingActivities);

        return {
            artistId: eventData?.artist?.id || "",
            name: "",
            description: "",
            startTimeTs: startDate,
            endTimeTs: null,
            startTimeZone: smartTimezone,
            endTimeZone: smartTimezone,
            type: "",
            eventId: eventData?.id || "",
            locationId: "",
            endLocationId: "",
            status: "CONFIRMED",
            routePolyline: "",
            distance: "",
            duration: "",
            vehicle: null,
            drivingTime: 0,
            createdAt: null,
            createdBy: null,
            event: eventData || null,
        };
    };

    const [artists, setArtists] = useState([]);
    const [events, setEvents] = useState([]);
    const [nearbyEvents, setNearbyEvents] = useState([]);
    const [fullActivityData, setFullActivityData] = useState(null);

    const [currentActivity, setCurrentActivity] = useState(() => {
        if (activity?.id) {
            return activity;
        }
        return createEmptyActivity(selectedDate, event, existingActivities);
    });

    const [hasBeenEdited, setHasBeenEdited] = useState(false);
    const lastInitializedId = useRef(null);

    // Update currentActivity when full details are fetched
    useEffect(() => {
        if (fullActivityData && fullActivityData.id === currentActivity?.id) {
            setCurrentActivity(prev => {
                // If user has already started editing name/description, don't overwrite them
                // unless they were empty before.
                const shouldOverwriteName = !hasBeenEdited || !prev.name;
                const shouldOverwriteDesc = !hasBeenEdited || !prev.description;

                // Normalize: ActivityFragment aliases location as startLocation
                const fullLocation = fullActivityData.location || fullActivityData.startLocation;
                const fullEndLocation = fullActivityData.endLocation;
                const fullLocationId = fullActivityData.locationId || fullLocation?.id || prev.locationId;
                const fullEndLocationId = fullActivityData.endLocationId || fullEndLocation?.id || prev.endLocationId;

                return {
                    ...fullActivityData,
                    ...prev, // Keep current edits
                    // Conditionally bring in full data fields if they weren't edited
                    name: shouldOverwriteName ? fullActivityData.name : prev.name,
                    description: shouldOverwriteDesc ? fullActivityData.description : prev.description,
                    // Always bring in these from full data as they aren't easily "edited" in the same way
                    activityGroups_on_activity: fullActivityData.activityGroups_on_activity,
                    activityCrews_on_activity: fullActivityData.activityCrews_on_activity,
                    // Always prefer full data for locations (calendar data may be sparse)
                    location: fullLocation || prev.location,
                    endLocation: fullEndLocation || prev.endLocation,
                    locationId: fullLocationId,
                    endLocationId: fullEndLocationId,
                    // Ensure date objects are correctly instantiated
                    startTimeTs: fullActivityData.startTimeTs ? new Date(fullActivityData.startTimeTs) : prev.startTimeTs,
                    endTimeTs: fullActivityData.endTimeTs ? new Date(fullActivityData.endTimeTs) : prev.endTimeTs,
                    // Bring in vehicle, route, driver fields from full data
                    vehicle: fullActivityData.vehicle || prev.vehicle,
                    vehicleId: fullActivityData.vehicle?.id || fullActivityData.vehicleId || prev.vehicleId,
                    routePolyline: fullActivityData.routePolyline || prev.routePolyline,
                    routeDetails: fullActivityData.routeDetails || prev.routeDetails,
                    drivingTime: fullActivityData.drivingTime ?? prev.drivingTime,
                    mainDriverId: fullActivityData.mainDriver?.id || prev.mainDriverId,
                    doubleDriverId: fullActivityData.doubleDriver?.id || prev.doubleDriverId,
                    areDriversAllocated: fullActivityData.areDriversAllocated ?? prev.areDriversAllocated,
                    flightNumber: fullActivityData.flightNumber || prev.flightNumber,
                    needsBooking: fullActivityData.needsBooking ?? prev.needsBooking,
                    bookingStatus: fullActivityData.bookingStatus || prev.bookingStatus,
                    bookingAssignedToId: fullActivityData.bookingAssignedTo?.id || fullActivityData.bookingAssignedToId || prev.bookingAssignedToId
                };
            });
            // Also update selection states based on full data
            const groupIds = fullActivityData.activityGroups_on_activity?.map(ag => ag.group?.id).filter(id => !!id) || [];
            const crewIds = [...new Set(fullActivityData.activityCrews_on_activity?.map(ac => ac.crewUserId || ac.crew?.user?.id).filter(id => !!id))];

            const initialBookings = fullActivityData.bookings_on_activity?.map(b => ({
                id: b.id,
                reference: b.reference || "",
                crewIds: b.seats_on_booking?.map(s => s.crew?.userId || s.crew?.user?.id).filter(id => !!id) || [],
                existingSeats: b.seats_on_booking || []
            })) || [];
            const bookingUserIds = new Set(initialBookings.flatMap(b => b.crewIds));

            setSelectedGroupIds(groupIds);
            // Hide users already managed via Booking References from manual Select Users state
            setSelectedCrewIds(crewIds.filter(id => !bookingUserIds.has(id)));
            setBookings(initialBookings);

            // Initialize v2 stops/legs from routeDetails
            const rd = fullActivityData.routeDetails || activity?.routeDetails;
            if (rd) {
                const parsed = parseRouteDetails(rd);
                if (parsed.version === 2) {
                    setActivityStops(parsed.stops);
                    setActivityLegs(parsed.legs);
                }
            }
        }
    }, [fullActivityData]);

    const [isCreatingStart, setIsCreatingStart] = useState(false);
    const [isCreatingEnd, setIsCreatingEnd] = useState(false);
    const [segments, setSegments] = useState([]); // [{ type: 'NIGHTLINER'|'FERRY', duration, distance, start_location, end_location, name }]
    const [activityStops, setActivityStops] = useState([]); // v2 stops
    const [activityLegs, setActivityLegs] = useState([]); // v2 legs
    const [isSaving, setIsSaving] = useState(false);


    // Vehicle State
    const [vehicles, setVehicles] = useState([]);
    const [crewMembers, setCrewMembers] = useState([]);
    const [areDriversAllocated, setAreDriversAllocated] = useState(false);
    const [isDoubleDriver, setIsDoubleDriver] = useState(false);
    const [isCreatingVehicle, setIsCreatingVehicle] = useState(false);
    const [newVehicle, setNewVehicle] = useState({ name: "", registrationNumber: "", capacity: 0 });

    const [availableGroups, setAvailableGroups] = useState([]);
    // Only store IDs for selection
    const [selectedCrewIds, setSelectedCrewIds] = useState([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState([]);
    const [bookings, setBookings] = useState([]); // [{ id: UUID|null, reference: string, crewIds: string[], existingSeats: [] }]
    const [notifyUsers, setNotifyUsers] = useState(false);
    const [notifyMessage, setNotifyMessage] = useState("");
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);



    useEffect(() => {
        if (show && activity?.id) {
            const fetchFullActivity = async () => {
                try {
                    const { data } = await getActivityById({ activityId: activity.id });
                    if (data.activities && data.activities.length > 0) {
                        setFullActivityData(data.activities[0]);
                    }
                } catch (err) {
                    console.error("Failed to fetch full activity details", err);
                }
            };
            fetchFullActivity();
        } else if (!show) {
            setFullActivityData(null);
        }
    }, [show, activity?.id]);

    const previousActivityId = useRef(activity?.id);
    useEffect(() => {
        // Check if ID truly changed
        if (activity?.id !== previousActivityId.current) {
            if (activity) {
                // Only update from activity if the ID changed
                const groupIds = activity.activityGroups_on_activity?.map(ag => ag.group?.id).filter(id => !!id) || [];
                const crewIds = [...new Set(activity.activityCrews_on_activity?.map(ac => ac.crew?.user?.id || ac.crewUserId).filter(id => !!id))];

                const initBookings = activity.bookings_on_activity?.map(b => ({
                    id: b.id,
                    reference: b.reference || "",
                    crewIds: b.seats_on_booking?.map(s => s.crew?.userId || s.crew?.user?.id).filter(id => !!id) || [],
                    existingSeats: b.seats_on_booking || []
                })) || [];
                const bookingUserIds = new Set(initBookings.flatMap(b => b.crewIds));

                setSelectedGroupIds(groupIds);
                setSelectedCrewIds(crewIds.filter(id => !bookingUserIds.has(id)));
                setBookings(initBookings);
            } else {
                setSelectedGroupIds([]);
                setSelectedCrewIds([]);
                setBookings([]);
            }
            previousActivityId.current = activity?.id;
        }
    }, [activity?.id, show]); // Only change when ID changes or modal opens

    const saveParticipants = async (activityId) => {
        // Use fullActivityData for initial state if available, otherwise fallback to prop
        const initialGroups = fullActivityData?.activityGroups_on_activity || activity?.activityGroups_on_activity || [];
        const initialGroupIds = initialGroups.map(ag => ag.group.id);
        const groupsToAdd = selectedGroupIds.filter(id => !initialGroupIds.includes(id));
        const groupsToRemove = initialGroupIds.filter(id => !selectedGroupIds.includes(id));

        for (const gid of groupsToAdd) {
            try { await addActivityGroup({ activityId, groupId: gid }); } catch (e) { console.error("Error adding group", e); }
        }
        for (const gid of groupsToRemove) {
            try { await removeActivityGroup({ activityId, groupId: gid }); } catch (e) { console.error("Error removing group", e); }
        }

        // Automatically include all users from Booking References (for FLIGHT) and Stops in activity.users (activityCrews_on_activity)
        const bookingCrewIds = currentActivity.type === 'FLIGHT'
            ? bookings.flatMap(b => b.crewIds || []).filter(Boolean)
            : [];
        const stopCrewIds = activityStops.flatMap(s => s.crewIds || []).filter(Boolean);
        const effectiveCrewIds = [...new Set([...selectedCrewIds, ...bookingCrewIds, ...stopCrewIds])];

        const initialCrews = fullActivityData?.activityCrews_on_activity || activity?.activityCrews_on_activity || [];
        const initialCrewIds = [...new Set(initialCrews.map(ac => ac.crewUserId || ac.crew?.user?.id).filter(id => !!id))];
        const crewToAdd = effectiveCrewIds.filter(id => !initialCrewIds.includes(id));
        const crewToRemove = initialCrewIds.filter(id => !effectiveCrewIds.includes(id));

        const artistId = currentActivity.artistId;
        const eventId = currentActivity.eventId;
        if (artistId) {
            // Add any selected crew members who are not yet in the event
            for (const uid of effectiveCrewIds) {
                const cm = crewPickerOptions.find(c => c.user?.id === uid || c.id === uid);
                if (cm && !cm.inEvent && eventId) {
                    try { 
                        await addEventCrew({ eventId, userId: uid, artistId }); 
                    } catch (e) { console.error("Error adding event crew", e); }
                }
            }

            for (const uid of crewToAdd) {
                try { await addCrewMemberToActivity({ activityId, userId: uid, artistId }); } catch (e) { console.error("Error adding crew", e); }
            }
            for (const uid of crewToRemove) {
                try { await removeCrewMemberFromActivity({ activityId, userId: uid, artistId }); } catch (e) { console.error("Error removing crew", e); }
            }

            if (currentActivity.type === 'FLIGHT') {
                const initialBookings = fullActivityData?.bookings_on_activity || activity?.bookings_on_activity || [];

                // Delete removed or duplicate bookings from database
                const activeBookingIds = new Set(bookings.map(b => b.id).filter(Boolean));
                for (const initialBooking of initialBookings) {
                    if (initialBooking.id && !activeBookingIds.has(initialBooking.id)) {
                        for (const seat of initialBooking.seats_on_booking || []) {
                            try { await deleteSeat({ id: seat.id }); } catch (e) { }
                        }
                        try { await deleteBooking({ id: initialBooking.id }); } catch (e) { }
                    }
                }

                // Create/Update Bookings
                for (const b of bookings) {
                    let bookingId = b.id;
                    if (!bookingId) {
                        try {
                            const res = await createBooking({
                                activityId: activityId,
                                reference: b.reference,
                                status: "CONFIRMED"
                            });
                            bookingId = res.data.booking_insert.id;
                            b.id = bookingId;
                        } catch (e) { console.error("Failed to create booking", e); }
                    } else if (b.reference !== (initialBookings.find(ib => ib.id === bookingId)?.reference)) {
                        try { await updateBooking({ id: bookingId, reference: b.reference }); } catch (e) { }
                    }

                    if (bookingId) {
                        const initialSeatIds = b.existingSeats?.map(s => s.crew?.userId) || [];
                        const bookingCrewToAdd = b.crewIds.filter(id => !initialSeatIds.includes(id));
                        const bookingCrewToRemove = initialSeatIds.filter(id => !b.crewIds.includes(id));

                        for (const uid of bookingCrewToAdd) {
                            try { await createSeat({ bookingId, crewUserId: uid, crewArtistId: artistId, status: "CONFIRMED" }); } catch (e) { }
                        }
                        for (const uid of bookingCrewToRemove) {
                            const seat = b.existingSeats?.find(s => s.crew?.userId === uid);
                            if (seat) {
                                try { await deleteSeat({ id: seat.id }); } catch (e) { }
                            }
                        }
                    }
                }
            }
        }
    };

    // Route Calculation Settings
    const [calcSettings, setCalcSettings] = useState({
        speed: 80,
        enableBusRules: true,
        driverType: 'SINGLE' // 'SINGLE' or 'DOUBLE'
    });

    // Route & Map State
    const [directionsResult, setDirectionsResult] = useState(null);
    const [routeIndex, setRouteIndex] = useState(0);
    const [waypoints, setWaypoints] = useState([]); // Array of location objects
    const [calculating, setCalculating] = useState(false);

    // Reset directions result when opening a new activity
    useEffect(() => {
        setDirectionsResult(null);
        setRouteIndex(0);
        setWaypoints([]);
        setActivityStops([]);
        setActivityLegs([]);
    }, [activity?.id, show]);

    const decodedPath = useMemo(() => {
        console.log("ActivityModal: Calc decodedPath. Polyline exists?", !!currentActivity.routePolyline, "Geometry loaded?", !!(window.google?.maps?.geometry));
        if (currentActivity.routePolyline && window.google?.maps?.geometry) {
            try {
                const path = window.google.maps.geometry.encoding.decodePath(currentActivity.routePolyline);
                console.log("ActivityModal: Decoded path length:", path?.length);
                return path;
            } catch (e) {
                console.error("Failed to decode route polyline:", e);
                return null;
            }
        }
        return null;
    }, [currentActivity.routePolyline, isLoaded]); // Re-calc if polyline changes or maps loaded

    // Route Calculation Logic
    const updateActivityWithRoute = (result, index) => {
        if (!result || !result.routes[index]) return;
        const route = result.routes[index];

        let totalDistanceMeters = 0;
        let googleDurationSeconds = 0;
        route.legs.forEach(l => {
            totalDistanceMeters += l.distance.value;
            googleDurationSeconds += l.duration.value;
        });

        // 1. Calculate Driving Time
        // Use the slower of Google's estimate (congestion/roads) or user's max speed (only for buses)
        const isBusType = ['NIGHTLINER', 'BUS', 'BUS_CALL'].includes(currentActivity.type);
        const googleSpeedMs = totalDistanceMeters / googleDurationSeconds;
        const userMaxSpeedMs = (calcSettings.speed * 1000) / 3600;
        const effectiveSpeedMs = (isBusType && calcSettings.speed > 0) ? Math.min(googleSpeedMs, userMaxSpeedMs) : googleSpeedMs;

        let totalSeconds = 0;

        // 3. Generate Route Details (JSON) with offsets
        const allSteps = route.legs.flatMap(l => l.steps);
        const segments = [];
        let currentType = null;
        let currentSeg = null;
        let accumulatedTime = 0; // seconds from start using effective speed

        allSteps.forEach((step) => {
            const isFerry = step.maneuver === 'ferry' || step.maneuver === 'ferry-on' || step.maneuver === 'ferry-off' || step.instructions?.toLowerCase().includes('ferry');
            const type = isFerry ? 'FERRY' : 'DRIVE';

            // For driving, we scale the duration if the effective speed is lower than the step speed
            // step.duration.value is Google's duration. step.distance.value is meters.
            // If type is DRIVE, we calculate duration based on effectiveSpeedMs if it's slower.
            // Actually, simplest implies: adjust DRIVE segments to match our total calculation style.
            let stepDuration = step.duration.value;
            if (type === 'DRIVE') {
                // Calculate expected time at current effective speed
                const myTime = step.distance.value / effectiveSpeedMs;
                // Use the larger duration (slower time) to be safe
                stepDuration = Math.max(step.duration.value, myTime);
            }

            if (currentType !== type) {
                if (currentSeg) segments.push(currentSeg);
                currentType = type;
                currentSeg = {
                    type,
                    description: isFerry ? 'Ferry Crossing' : 'Driving',
                    instruction: step.instructions,
                    startLocation: step.start_location ? { lat: step.start_location.lat(), lng: step.start_location.lng() } : null,
                    startTimeOffset: accumulatedTime, // seconds from start
                    duration: 0,
                    distance: 0
                };
            }
            currentSeg.duration += stepDuration;
            currentSeg.distance += step.distance.value;
            accumulatedTime += stepDuration;
        });
        if (currentSeg) segments.push(currentSeg);

        totalSeconds = accumulatedTime;
        let infoText = "";

        // 2. Apply Bus Rules (Breaks added to total)
        if (isBusType && calcSettings.enableBusRules && calcSettings.driverType === 'SINGLE') {
            const drivingHours = totalSeconds / 3600;
            const limit = 4.5;
            const breakDuration = 45 * 60;
            const numBreaks = Math.floor((drivingHours - 0.001) / limit);
            if (numBreaks > 0) {
                totalSeconds += numBreaks * breakDuration;
                infoText = ` (+ ${numBreaks} x 45m breaks)`;
            }
        } else if (isBusType && calcSettings.enableBusRules && calcSettings.driverType === 'DOUBLE') {
            infoText = " (Double Driver)";
        }

        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const durationText = `${h} h ${m} min${infoText}`;

        // Update Activity State
        setCurrentActivity(prev => ({
            ...prev,
            distance: (totalDistanceMeters / 1000).toFixed(1) + " km",
            duration: durationText,
            drivingTime: Math.round(totalSeconds / 60), // minutes
            routePolyline: route.overview_polyline,
            routeDetails: JSON.stringify(segments),
            endTimeTs: prev.startTimeTs ? moment(prev.startTimeTs).add(totalSeconds, 'seconds').toDate() : null
        }));
    };

    const handleCalculateRoute = () => {
        if (!window.google || !currentActivity.location || !currentActivity.endLocation) return;
        setCalculating(true);

        const directionsService = new window.google.maps.DirectionsService();
        const waypointsList = waypoints.map(wp => ({
            location: { lat: wp.latitude, lng: wp.longitude },
            stopover: true
        }));

        directionsService.route(
            {
                origin: { lat: currentActivity.location.latitude, lng: currentActivity.location.longitude },
                destination: { lat: currentActivity.endLocation.latitude, lng: currentActivity.endLocation.longitude },
                waypoints: waypointsList,
                travelMode: window.google.maps.TravelMode.DRIVING,
                provideRouteAlternatives: true
            },
            (result, status) => {
                setCalculating(false);
                if (status === window.google.maps.DirectionsStatus.OK) {
                    setDirectionsResult(result);
                    setRouteIndex(0);
                    updateActivityWithRoute(result, 0);
                } else {
                    console.error("Directions request failed due to " + status);
                }
            }
        );
    };

    // Update activity and creation state when props change
    useEffect(() => {
        if (!show) {
            lastInitializedId.current = null;
            setHasBeenEdited(false);
            return;
        }

        const currentId = activity?.id || 'new';
        if (lastInitializedId.current === currentId) return;

        let itemsToSet = null;
        if (activity) {
            // Edit mode OR pre-filled create mode (e.g. from Add Above/Below)
            const artistId = activity.artistId || activity.artist?.id || event?.artist?.id || "";
            const eventId = activity.eventId || activity.event?.id || event?.id || "";

            // If it's a new activity (no ID), start with defaults but let the pass-in object override
            const defaults = createEmptyActivity(selectedDate, event, existingActivities);

            itemsToSet = {
                ...defaults,
                ...activity,
                artistId: artistId || defaults.artistId,
                eventId: eventId || defaults.eventId,
                // Derive locationId/endLocationId from location objects if not top-level
                locationId: activity.locationId || activity.location?.id || defaults.locationId,
                endLocationId: activity.endLocationId || activity.endLocation?.id || defaults.endLocationId,
                // Robustly handle startTimeTs conversion to Date
                startTimeTs: activity.startTimeTs ? new Date(activity.startTimeTs) : defaults.startTimeTs,
                drivingTime: activity.drivingTime || activity.drivingTime === 0 ? activity.drivingTime : defaults.drivingTime
            };
        } else if (selectedDate || event) {
            // Basic Create mode from scratch
            itemsToSet = createEmptyActivity(selectedDate, event, existingActivities);
        }

        if (itemsToSet) {
            setCurrentActivity(itemsToSet);
            setHasBeenEdited(false);
            lastInitializedId.current = currentId;
            // Initialize create mode states: true only if name exists but ID is missing (both top-level and in object)
            setIsCreatingStart((!itemsToSet.locationId && !itemsToSet.location?.id) && !!itemsToSet.location?.name);
            setIsCreatingEnd((!itemsToSet.endLocationId && !itemsToSet.endLocation?.id) && !!itemsToSet.endLocation?.name);
        }
    }, [show, activity, event, selectedDate, existingActivities]);


    useEffect(() => {
        if (show && event?.artist?.id) {
            setCurrentActivity((prev) => ({
                ...prev,
                artistId: event.artist.id
            }));
        }
    }, [show, event?.artist?.id]);

    const { data: artistsData } = useArtists({ enabled: show });
    useEffect(() => {
        if (artistsData) {
            setArtists(artistsData);
            // After setting artists, set artistId if there are multiple artists
            if (artistsData.length > 1 && !currentActivity.artistId) {
                setProperty("artistId", artistsData[0].id);
            }
            if (artistsData.length > 0) {
                // We need artistId to fetch vehicles. If currentActivity has it, great.
                // If not, we just set it above, so we might need to wait for render cycle or use the value directly.
                const aid = currentActivity.artistId || artistsData[0].id;
                if (aid) fetchVehicles(aid);
            }
        }
    }, [artistsData, show]);

    const fetchVehicles = async (artistId) => {
        try {
            const { data } = await getVehicles({ artistId });
            setVehicles(data.vehicles || []);
        } catch (e) {
            console.error("Failed to fetch vehicles", e);
        }
    };

    useEffect(() => {
        if (show && currentActivity?.artistId) {
            getVehicles({ artistId: currentActivity.artistId }).then(({ data }) => {
                setVehicles(data.vehicles);
            });
            getArtistCrewMembers({ artistId: currentActivity.artistId }).then(({ data }) => {
                const uniqueMembers = [];
                const seenIds = new Set();
                (data.crewMembers || []).forEach(cm => {
                    if (cm.user && !seenIds.has(cm.user.id)) {
                        uniqueMembers.push(cm);
                        seenIds.add(cm.user.id);
                    }
                });
                setCrewMembers(uniqueMembers);
            });
            getCrewMemberGroups({ artistId: currentActivity.artistId }).then(({ data }) => setAvailableGroups(data.crewMemberGroups || []));
        }
    }, [show, currentActivity?.artistId]);

    // Initialize driver states from activity
    useEffect(() => {
        if (activity) {
            setAreDriversAllocated(activity.areDriversAllocated || false);
            setIsDoubleDriver(!!activity.doubleDriver);
        }
    }, [activity]);

    // Fetch Nearby Events (Days before/after)
    useEffect(() => {
        if (show && currentActivity.artistId && currentActivity.startTimeTs) {
            const fetchNearby = async () => {
                const dateStr = moment(currentActivity.startTimeTs).format('YYYY-MM-DD');
                // Range: -3 days to +3 days to be safe and provide context
                const start = moment(currentActivity.startTimeTs).add(-3, 'days').format('YYYY-MM-DD');
                const end = moment(currentActivity.startTimeTs).add(3, 'days').format('YYYY-MM-DD');

                try {
                    const { data } = await getEventsRange({ artistId: currentActivity.artistId, startDate: start, endDate: end });
                    if (data?.events) {
                        setNearbyEvents(data.events);
                    }
                } catch (error) {
                    console.error("Error fetching nearby events:", error);
                }
            };
            fetchNearby();
        }
    }, [show, currentActivity.artistId, moment(currentActivity.startTimeTs).format('YYYY-MM-DD')]);

    const artistEvents = useEvents({ enabled: show });

    useEffect(() => {
        if (!event && currentActivity?.artistId && artistEvents.data) {
            const eventsForArtist = artistEvents.data.filter(e => e.artist?.id === currentActivity.artistId);
            setEvents(eventsForArtist);

            const selected = eventsForArtist.find(e => e.id === currentActivity.eventId);
            if (selected?.date && !currentActivity.startTimeTs) {
                setProperty("startTimeTs", new Date(selected.date));
            }
        }
    }, [event, currentActivity?.artistId, artistEvents.data]);

    useEffect(() => {
        if (
            currentActivity &&
            (!currentActivity.artistId || currentActivity.artistId === "") &&
            artists.length === 1
        ) {
            setProperty("artistId", artists[0].id);
        }
    }, [artists, currentActivity]);

    // Available locations derivation (Consolidated)
    const availableLocations = useMemo(() => {
        const map = new Map();

        const addLoc = (l, context = null) => {
            if (l && l.id) {
                // If already exists, updated context might be better (e.g. Nearby context overrides general)
                const existing = map.get(l.id);
                if (!existing || (context && !existing.context)) {
                    map.set(l.id, { ...l, context: context || existing?.context });
                }
            }
        };

        // 1. Current Activity Locs
        if (currentActivity?.location) addLoc(currentActivity.location);
        if (currentActivity?.endLocation) addLoc(currentActivity.endLocation);

        // 2. Nearby Events (Priority)
        if (nearbyEvents) {
            nearbyEvents.forEach(ev => {
                const dayDiff = moment(ev.date).diff(moment(currentActivity.startTimeTs).startOf('day'), 'days');
                let dayLabel = "";
                if (dayDiff === 0) dayLabel = "(Today)";
                else if (dayDiff === -1) dayLabel = "(Yesterday)";
                else if (dayDiff === 1) dayLabel = "(Tomorrow)";
                else dayLabel = `(${moment(ev.date).format('MMM D')})`;

                const baseContext = `${dayLabel} - ${ev.name || 'Event'}`;

                if (ev.venue) {
                    addLoc(ev.venue.location, baseContext);
                    addLoc(ev.venue.eventVenueLocation, baseContext);
                    addLoc(ev.venue.venueLocation, baseContext);
                }

                if (ev.eventLocations_on_event) {
                    ev.eventLocations_on_event.forEach(el => {
                        if (el.location) {
                            // Use the event location name (e.g. "Hotel") as context if present
                            const elContext = `${dayLabel} - ${el.name || ev.name || 'Event'}`;
                            addLoc(el.location, elContext);
                        }
                    });
                }
            });
        }

        // 3. General Events
        if (events) {
            events.forEach(ev => {
                if (ev.venue) {
                    addLoc(ev.venue.location);
                    addLoc(ev.venue.eventVenueLocation);
                    addLoc(ev.venue.venueLocation);
                }
                if (ev.eventLocations_on_event) {
                    ev.eventLocations_on_event.forEach(el => {
                        addLoc(el.location);
                    });
                }
            });
        }

        const sorted = Array.from(map.values()).sort((a, b) => {
            // Priority to context (Nearby)
            if (a.context && !b.context) return -1;
            if (!a.context && b.context) return 1;
            return (a.name || "").localeCompare(b.name || "");
        });

        return sorted;
    }, [events, nearbyEvents, currentActivity?.location, currentActivity?.endLocation, currentActivity.startTimeTs]);


    const handleCloseModal = () => {
        setCurrentActivity(createEmptyActivity(selectedDate, event, existingActivities));
        setIsCreatingStart(false);
        setIsCreatingEnd(false);
        setNotifyUsers(false);
        setNotifyMessage("");
        handleClose();
    };

    const handleSubmit = async () => {
        if (!currentActivity.artistId) {
            alert("Activity must be linked to an artist!");
            return;
        }

        setIsSaving(true);
        try {
            let submissionActivity = { ...currentActivity };

            if (submissionActivity.locationId === "") delete submissionActivity.locationId;
            if (!submissionActivity.locationId && !submissionActivity.location?.name) delete submissionActivity.location;

            // Clear end location for activity types that don't support it
            const typesWithEndLocation = ['TRANSPORT', 'NIGHTLINER', 'CAR', 'BUS', 'TAXI', 'FLIGHT', 'FERRY', 'WALK'];
            if (!typesWithEndLocation.includes(submissionActivity.type)) {
                submissionActivity.endLocationId = "";
                delete submissionActivity.endLocation;
            }

            if (submissionActivity.endLocationId === "") delete submissionActivity.endLocationId;
            if (!submissionActivity.endLocationId && !submissionActivity.endLocation?.name) delete submissionActivity.endLocation;

            const newActivity = {
                artistId: submissionActivity.artistId,
                name: submissionActivity.name,
                description: submissionActivity.description,
                startTimeTs: submissionActivity.startTimeTs.toISOString(),
                startTimeZone: submissionActivity.startTimeZone,
                eventId: submissionActivity.eventId,
                locationId: submissionActivity.locationId || "",
                endLocationId: submissionActivity.endLocationId || "",
                status: submissionActivity.status,
                type: submissionActivity.type,
                flightNumber: submissionActivity.flightNumber,
                routePolyline: submissionActivity.routePolyline,
                distance: submissionActivity.distance,
                duration: submissionActivity.duration,
                vehicleId: submissionActivity.vehicleId || null,
                drivingTime: parseInt(submissionActivity.drivingTime) || 0
            };

            if (
                submissionActivity.endTimeTs instanceof Date &&
                !isNaN(submissionActivity.endTimeTs) &&
                submissionActivity.endTimeTs !== ""
            ) {
                newActivity['endTimeZone'] = submissionActivity.endTimeZone || submissionActivity.startTimeZone;
                newActivity['endTimeTs'] = submissionActivity.endTimeTs.toISOString();
            } else {
                newActivity['endTimeTs'] = null;
                newActivity['endTimeZone'] = null;
            }

            // For updates, send null to clear FK fields; for creates, delete them
            const isUpdate = !!(submissionActivity?.id);
            if (newActivity.locationId == "") {
                if (isUpdate) newActivity.locationId = null;
                else delete newActivity.locationId;
            }
            if (newActivity.endLocationId == "") {
                if (isUpdate) newActivity.endLocationId = null;
                else delete newActivity.endLocationId;
            }
            if (submissionActivity.startTimeTs == submissionActivity.endTimeTs) delete newActivity.endTimeTs;
            if (submissionActivity.eventId == "") delete newActivity.eventId;
            if (submissionActivity.flightNumber == "") delete newActivity.flightNumber;

            if (submissionActivity.eventId) {
                newActivity['eventId'] = submissionActivity.eventId;
            }

            // --- SPLIT ACTIVITY LOGIC ---
            if (segments.length > 1 && !submissionActivity.id) {
                console.log("Splitting activity into", segments.length, "parts");
                let currentTime = moment.tz(submissionActivity.startTimeTs, submissionActivity.startTimeZone);

                let lastEndLocId = submissionActivity.locationId;

                for (let i = 0; i < segments.length; i++) {
                    const seg = segments[i];
                    const segDuration = seg.duration; // in seconds
                    const segEnd = currentTime.clone().add(segDuration, 'seconds');

                    let segStartLocId = lastEndLocId;
                    let segEndLocId = "";

                    if (i === segments.length - 1) {
                        segEndLocId = submissionActivity.endLocationId;
                    } else {
                        // Create intermediate location for the end of this segment
                        const { locationId: portLocId } = await resolvePlace({
                            name: `${seg.type === 'FERRY' ? 'Arrival Port' : 'Ferry Port'}`,
                            latitude: seg.end_location.lat(),
                            longitude: seg.end_location.lng(),
                        }, { artistId: submissionActivity.artistId });
                        segEndLocId = portLocId;
                        lastEndLocId = segEndLocId;
                    }

                    const partActivity = {
                        ...newActivity,
                        name: `${submissionActivity.name} (${seg.type === 'FERRY' ? 'Ferry' : 'Nightliner'} ${Math.floor(i / 2) + 1})`,
                        type: seg.type,
                        startTimeTs: currentTime.toDate().toISOString(),
                        endTimeTs: segEnd.toDate().toISOString(),
                        startTimeZone: submissionActivity.startTimeZone,
                        endTimeZone: submissionActivity.endTimeZone,
                        locationId: segStartLocId,
                        endLocationId: segEndLocId,
                        distance: `${(seg.distance / 1000).toFixed(1)} km`,
                        duration: `${Math.floor(segDuration / 3600)} h ${Math.floor((segDuration % 3600) / 60)} min`
                    };

                    const { data: partData } = await createActivity(partActivity);

                    if (typeof addToReturn === "function") {
                        addToReturn({
                            ...partActivity,
                            id: partData.activity_insert.id
                        });
                    }

                    currentTime = segEnd;
                }
                handleCloseModal();
                return;
            }

            const sendNotifications = (activityId, isNew) => {
                if (!notifyUsers || !activityId) return;
                const notifyParticipants = httpsCallable(functions, "notifyActivityParticipants");
                void notifyParticipants({
                    activityId,
                    isNew,
                    notifyMessage: notifyMessage || "",
                }).catch((err) => console.warn("Async activity push trigger failed:", err));
            };

            const subscribeAllFlightAlerts = (activityId, baseFlightNumber, startTs) => {
                if (newActivity.type !== 'FLIGHT') return;
                const allFlightNums = new Set([
                    ...parseFlightNumbers(baseFlightNumber || ''),
                    ...activityLegs.flatMap(l => parseFlightNumbers(l.flightNumber || ''))
                ]);
                if (allFlightNums.size === 0) return;
                const subscribe = httpsCallable(functions, 'subscribeFlightAlert');
                const flightDate = moment(startTs).format('YYYY-MM-DD');
                for (const fn of allFlightNums) {
                    void subscribe({ flightNumber: fn, date: flightDate, activityId }).catch((e) =>
                        console.warn('Flight alert subscribe failed (non-blocking):', fn, e)
                    );
                }
            };

            if (submissionActivity != null && submissionActivity.id != null) {
                newActivity['id'] = submissionActivity.id;

                // Explicitly include fields that might be missing from newActivity but present in submissions state
                newActivity.vehicleId = submissionActivity.vehicleId;
                newActivity.drivingTime = parseInt(submissionActivity.drivingTime) || 0;
                // Serialize v2 stops/legs if present, otherwise use existing routeDetails
                if (activityStops.length > 0) {
                    newActivity.routeDetails = serializeRouteDetails(activityStops, activityLegs);
                } else {
                    newActivity.routeDetails = submissionActivity.routeDetails;
                }

                // Driver fields
                newActivity.mainDriverId = submissionActivity.mainDriverId;
                newActivity.doubleDriverId = submissionActivity.doubleDriverId;
                newActivity.areDriversAllocated = areDriversAllocated;
                
                // Helper to clean up fields not accepted by DataConnect mutation schema
                const sanitizePayload = (obj) => {
                    const clean = { ...obj };
                    delete clean.needsBooking;
                    delete clean.bookingStatus;
                    delete clean.bookingAssignedToId;
                    delete clean.event;
                    delete clean.artist;
                    delete clean.location;
                    delete clean.endLocation;
                    delete clean.users_on_activity;
                    delete clean.groups_on_activity;
                    delete clean.bookings_on_activity;
                    delete clean.comments_on_activity;
                    delete clean.files_on_activity;
                    return clean;
                };

                await updateActivity(sanitizePayload(newActivity));
                await saveParticipants(newActivity.id);
                if (notifyUsers) {
                    sendNotifications(newActivity.id, false);
                }

                subscribeAllFlightAlerts(newActivity.id, newActivity.flightNumber, newActivity.startTimeTs);

                if (typeof addToReturn === "function") {
                    addToReturn({
                        ...submissionActivity,
                        id: submissionActivity.id,
                        ...newActivity // ensure return has latest fields
                    });
                }
                handleCloseModal();
                refreshData(currentActivity.artistId);
                return;
            }

            // Create
            // Ensure fields are present for create too
            newActivity.vehicleId = submissionActivity.vehicleId;
            newActivity.drivingTime = parseInt(submissionActivity.drivingTime) || 0;
            // Serialize v2 stops/legs if present, otherwise use existing routeDetails
            if (activityStops.length > 0) {
                newActivity.routeDetails = serializeRouteDetails(activityStops, activityLegs);
            } else {
                newActivity.routeDetails = submissionActivity.routeDetails;
            }
            newActivity.mainDriverId = submissionActivity.mainDriverId;
            newActivity.doubleDriverId = submissionActivity.doubleDriverId;
            newActivity.areDriversAllocated = areDriversAllocated;

            newActivity.needsBooking = submissionActivity.needsBooking || false;
            newActivity.bookingStatus = submissionActivity.bookingStatus || "PENDING";
            newActivity.bookingAssignedToId = submissionActivity.bookingAssignedToId || null;

            const sanitizePayload = (obj) => {
                const clean = { ...obj };
                delete clean.needsBooking;
                delete clean.bookingStatus;
                delete clean.bookingAssignedToId;
                delete clean.event;
                delete clean.artist;
                delete clean.location;
                delete clean.endLocation;
                delete clean.users_on_activity;
                delete clean.groups_on_activity;
                delete clean.bookings_on_activity;
                delete clean.comments_on_activity;
                delete clean.files_on_activity;
                return clean;
            };

            const { data } = await createActivity(sanitizePayload(newActivity));
            const createdId = data?.activity_insert?.id;
            if (createdId) {
                await saveParticipants(createdId);
                if (notifyUsers) {
                    sendNotifications(createdId, true);
                }

                subscribeAllFlightAlerts(createdId, newActivity.flightNumber, newActivity.startTimeTs);
            }
            if (typeof addToReturn === "function") {
                addToReturn({
                    ...submissionActivity,
                    id: submissionActivity.id || createdId
                });
            }
            handleCloseModal();
            refreshData(currentActivity.artistId);
        } catch (error) {
            console.error("Error saving activity:", error);
            alert("Failed to save activity");
        } finally {
            setIsSaving(false);
        }
    };


    const hasDeletePermission = useMemo(() => {
        // Use the artist attached to the activity or the passed-in event
        const artist = currentActivity?.artist || event?.artist;
        if (!artist?.crewMembers_on_artist) {
            // If we don't have crew info on the object, we can't be sure, 
            // but we can fallback to the artistId check if needed.
            return true; // Defaulting to true as the backend will block it anyway if insufficient
        }
        const myUid = getMe()?.uid;
        const crewMember = artist.crewMembers_on_artist.find(cm => cm.user?.id === myUid || cm.user === myUid);
        return crewMember ? (crewMember.itinerary >= 2) : false;
    }, [currentActivity, event]);

    const handleDelete = () => {
        setShowDeleteConfirm(true);
    };

    const executeDelete = async () => {
        try {
            await deleteActivity({ id: currentActivity.id });
            if (typeof addToReturn === "function") {
                addToReturn({ ...currentActivity, _deleted: true });
            }
            setShowDeleteConfirm(false);
            handleCloseModal();
            refreshData(currentActivity.artistId);
        } catch (error) {
            console.error("Error deleting activity:", error);
            alert("Failed to delete activity.");
        }
    };

    const setProperty = (key, value) => {
        setHasBeenEdited(true);
        setCurrentActivity((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const [searchingFlight, setSearchingFlight] = useState(false);

    const ensureTourLocation = async (locObj, isStart = true) => {
        if (!locObj || !locObj.latitude || !locObj.longitude) return { id: null, loc: locObj };
        try {
            // Reuses the location if this place is already in Tourminator
            const { locationId: createdId } = await resolvePlace({
                name: locObj.name,
                city: locObj.city || null,
                country: locObj.country || null,
                latitude: locObj.latitude,
                longitude: locObj.longitude,
            }, { artistId: currentActivity.artistId });
            if (createdId) {
                return { id: createdId, loc: { ...locObj, id: createdId } };
            }
        } catch (e) {
            console.warn("Auto-create location failed, showing dialog:", e);
            if (isStart) setIsCreatingStart(true);
            else setIsCreatingEnd(true);
        }
        return { id: null, loc: locObj };
    };

    const handleFlightSearch = async () => {
        const flightNums = parseFlightNumbers(currentActivity.flightNumber);
        if (flightNums.length === 0) {
            alert("Vennligst skriv inn et eller flere flightnummer (f.eks. SK455 eller SK455, SK1462)");
            return;
        }
        setSearchingFlight(true);
        try {
            // Single-leg flight search
            if (flightNums.length === 1) {
                const result = await searchFlight(currentActivity.startTimeTs, currentActivity.startTimeZone, flightNums[0]);
                if (result) {
                    const updates = {
                        flightNumber: result.flightNumber || flightNums[0],
                        startTimeTs: result.startTimeTs || currentActivity.startTimeTs,
                        endTimeTs: result.endTimeTs || currentActivity.endTimeTs,
                        startTimeZone: result.startTimeZone || currentActivity.startTimeZone,
                        endTimeZone: result.endTimeZone || result.startTimeZone || currentActivity.endTimeZone || currentActivity.startTimeZone,
                        distance: result.distance || currentActivity.distance,
                        duration: result.duration || currentActivity.duration,
                    };

                    if (result.location) {
                        const { id: startId, loc: startLoc } = await ensureTourLocation(result.location, true);
                        if (startId) updates.locationId = startId;
                        updates.location = startLoc;
                    }
                    if (result.endLocation) {
                        const { id: endId, loc: endLoc } = await ensureTourLocation(result.endLocation, false);
                        if (endId) updates.endLocationId = endId;
                        updates.endLocation = endLoc;
                    }

                    setCurrentActivity(prev => ({ ...prev, ...updates }));
                }
                return;
            }

            // Multi-leg flight search (2+ flight numbers)
            const results = [];
            const notFound = [];
            let refDate = currentActivity.startTimeTs || new Date();
            let refTz = currentActivity.startTimeZone || "UTC";

            for (let i = 0; i < flightNums.length; i++) {
                const fn = flightNums[i];
                let res = await searchFlight(refDate, refTz, fn, { silent: true });

                // If connecting flight departs after midnight (next day) or wasn't found on same day, check +1 day
                if (i > 0) {
                    const prevArrival = results[results.length - 1]?.endTimeTs;
                    const departsBeforePrevArrival = res?.startTimeTs && prevArrival && new Date(res.startTimeTs) < new Date(prevArrival);
                    if (!res || departsBeforePrevArrival) {
                        const nextDayDate = moment(refDate).tz(refTz).add(1, 'day').toDate();
                        const nextDayRes = await searchFlight(nextDayDate, refTz, fn, { silent: true });
                        if (nextDayRes) {
                            res = nextDayRes;
                        }
                    }
                }

                if (res) {
                    results.push({ ...res, requestedFlightNumber: fn });
                    if (res.endTimeTs) refDate = res.endTimeTs;
                    if (res.endTimeZone || res.endLocation?.timeZone) {
                        refTz = res.endTimeZone || res.endLocation.timeZone;
                    }
                } else {
                    notFound.push(fn);
                }
            }

            if (results.length === 0) {
                alert(`Ingen flightdata funnet for: ${flightNums.join(', ')}`);
                return;
            }
            if (notFound.length > 0) {
                alert(`Fant ${results.length} av ${flightNums.length} flights. Kunne ikke finne: ${notFound.join(', ')}`);
            }

            const firstFlight = results[0];
            const lastFlight = results[results.length - 1];

            const totalDistanceKm = results.reduce((sum, r) => sum + (r.distanceKm || 0), 0);
            let totalDurationStr = lastFlight.duration || currentActivity.duration;
            if (firstFlight.startTimeTs && lastFlight.endTimeTs) {
                const totalMins = moment(lastFlight.endTimeTs).diff(moment(firstFlight.startTimeTs), 'minutes');
                if (totalMins > 0) {
                    const hrs = Math.floor(totalMins / 60);
                    const rest = totalMins % 60;
                    totalDurationStr = `${hrs}t ${rest}m`;
                }
            }

            const updates = {
                flightNumber: results.map(r => r.flightNumber || r.requestedFlightNumber).join(', '),
                startTimeTs: firstFlight.startTimeTs || currentActivity.startTimeTs,
                endTimeTs: lastFlight.endTimeTs || currentActivity.endTimeTs,
                startTimeZone: firstFlight.startTimeZone || currentActivity.startTimeZone,
                endTimeZone: lastFlight.endTimeZone || lastFlight.startTimeZone || currentActivity.endTimeZone || currentActivity.startTimeZone,
                distance: totalDistanceKm > 0 ? `${Math.round(totalDistanceKm)} km` : (firstFlight.distance || currentActivity.distance),
                duration: totalDurationStr,
            };

            if (firstFlight.location) {
                const { id: startId, loc: startLoc } = await ensureTourLocation(firstFlight.location, true);
                if (startId) updates.locationId = startId;
                updates.location = startLoc;
            }
            if (lastFlight.endLocation) {
                const { id: endId, loc: endLoc } = await ensureTourLocation(lastFlight.endLocation, false);
                if (endId) updates.endLocationId = endId;
                updates.endLocation = endLoc;
            }

            // Build intermediate layover stops & legs for StopsEditor (v2)
            if (results.length > 1) {
                const newStops = [];
                for (let i = 0; i < results.length - 1; i++) {
                    const arrivingLeg = results[i];
                    const departingLeg = results[i + 1];
                    const layoverLoc = arrivingLeg.endLocation || departingLeg.location || null;
                    const layoverTz = arrivingLeg.endTimeZone || layoverLoc?.timeZone || departingLeg.startTimeZone || updates.startTimeZone;
                    const arrTimeIso = arrivingLeg.endTimeTs ? new Date(arrivingLeg.endTimeTs).toISOString() : null;
                    const depTimeIso = departingLeg.startTimeTs ? new Date(departingLeg.startTimeTs).toISOString() : arrTimeIso;

                    newStops.push({
                        id: `stop-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 4)}`,
                        type: 'LAYOVER',
                        location: layoverLoc,
                        arrivalTime: arrTimeIso,
                        time: depTimeIso,
                        timeZone: layoverTz,
                        crewIds: [],
                        notes: ''
                    });
                }

                const newLegs = results.map((r, i) => ({
                    from: i === 0 ? '__start__' : newStops[i - 1].id,
                    to: i === results.length - 1 ? '__end__' : newStops[i].id,
                    distance: r.distance || null,
                    duration: r.durationSeconds || null,
                    flightNumber: r.flightNumber || r.requestedFlightNumber || null,
                    polyline: null
                }));

                setActivityStops(newStops);
                setActivityLegs(newLegs);
            }

            setCurrentActivity(prev => ({ ...prev, ...updates }));
        } catch (error) {
            console.error("Flight search failed:", error);
            alert("Kunne ikke hente flightinformasjon.");
        } finally {
            setSearchingFlight(false);
        }
    };

    const handleTimezoneChange = (key, newZone) => {
        setCurrentActivity(prev => {
            const timeKey = key === 'startTimeZone' ? 'startTimeTs' : 'endTimeTs';
            if (!prev[timeKey]) return { ...prev, [key]: newZone };

            // Keep the digits the same (nominal time)
            const nominal = moment.tz(prev[timeKey], prev[key] || prev.startTimeZone).format('YYYY-MM-DDTHH:mm:ss');
            const newTs = moment.tz(nominal, newZone).toDate();

            return {
                ...prev,
                [key]: newZone,
                [timeKey]: newTs
            };
        });
    };


    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    // Filter crew members and add inEvent flag
    const availableCrewMembers = useMemo(() => {
        const activeEvent = event || activity?.event || events.find(e => e.id === currentActivity?.eventId);
        let list = crewMembers;

        let eventUserIds = new Set();
        if (activeEvent?.eventCrews_on_event && activeEvent.eventCrews_on_event.length > 0) {
            eventUserIds = new Set(
                activeEvent.eventCrews_on_event
                    .map(ec => ec.crew?.user?.id || ec.crew?.userId || ec.user?.id || ec.userId)
                    .filter(Boolean)
            );
        }

        const unique = [];
        const seen = new Set();
        list.forEach(cm => {
            const uid = cm.user?.id || cm.id;
            if (uid && !seen.has(uid) && eventUserIds.has(uid)) {
                seen.add(uid);
                unique.push({ ...cm, inEvent: true });
            }
        });

        return unique.sort((a, b) => {
            const nameA = a.user?.firstName || a.name || "";
            const nameB = b.user?.firstName || b.name || "";
            return nameA.localeCompare(nameB);
        });
    }, [crewMembers, event, activity, events, currentActivity?.eventId]);

    // Artist crew who aren't on the event yet. The user pickers offer them as "+ Add … to event" once
    // you type; saving the activity adds them to the event ("not yet in the event" in the save logic).
    const addableCrewMembers = useMemo(() => {
        if (!currentActivity?.eventId) return [];
        const inEvent = new Set(availableCrewMembers.map(cm => cm.user?.id || cm.id));
        return crewMembers
            .filter(cm => cm.user?.id && !inEvent.has(cm.user.id))
            .map(cm => ({ ...cm, inEvent: false }));
    }, [crewMembers, availableCrewMembers, currentActivity?.eventId]);
    const crewPickerOptions = useMemo(
        () => [...availableCrewMembers, ...addableCrewMembers],
        [availableCrewMembers, addableCrewMembers]
    );
    const filterCrewOptions = (options, state) =>
        defaultCrewFilter(options, state).filter(o => o.inEvent || state.inputValue.trim());

    // Crew IDs managed via Flight Booking References (automatically saved to activity.users, hidden in Select Users UI)
    const bookingCrewIdsSet = useMemo(() => {
        if (currentActivity?.type !== 'FLIGHT') return new Set();
        return new Set(bookings.flatMap(b => b.crewIds || []).filter(Boolean));
    }, [bookings, currentActivity?.type]);

    // Calculate affected users for UI feedback
    const affectedUsers = useMemo(() => {
        const stopCrewIds = activityStops.flatMap(s => s.crewIds || []).filter(Boolean);
        const allSpecificCrewIds = [...new Set([...selectedCrewIds, ...bookingCrewIdsSet, ...stopCrewIds])];

        // If no groups and no specific users are selected, everyone in the event is a participant.
        if (selectedGroupIds.length === 0 && allSpecificCrewIds.length === 0) {
            if (!event || !event.eventCrews_on_event) return [];
            // Get all users assigned to this event
            const eventCrewUsers = event.eventCrews_on_event.map(ec => ec.crew?.user || ec.user).filter(u => !!u);
            const unique = [];
            const seen = new Set();
            for (const u of eventCrewUsers) {
                if (!seen.has(u.id)) {
                    seen.add(u.id);
                    unique.push(u);
                }
            }
            return unique.sort((a, b) => (a.firstName || "").localeCompare(b.firstName || ""));
        }

        // Otherwise, calculate the specific users
        const unique = [];
        const seen = new Set();

        // 1. Add specific users (including booking references and stops)
        allSpecificCrewIds.forEach(id => {
            const cm = availableCrewMembers.find(c => c.user?.id === id) || crewMembers.find(c => c.user?.id === id);
            if (cm?.user && !seen.has(id)) {
                seen.add(id);
                unique.push(cm.user);
            }
        });

        // 2. Add users from groups
        selectedGroupIds.forEach(gid => {
            const membersInGroup = crewMembers.filter(cm => cm.group?.id === gid);
            membersInGroup.forEach(cm => {
                if (cm.user && !seen.has(cm.user.id)) {
                    seen.add(cm.user.id);
                    unique.push(cm.user);
                }
            });
        });

        return unique.sort((a, b) => (a.firstName || "").localeCompare(b.firstName || ""));
    }, [selectedGroupIds, selectedCrewIds, bookingCrewIdsSet, activityStops, event, crewMembers, availableCrewMembers]);

    if (currentActivity == null) {
        return null;
    }

    return (
        <Dialog
            open={show}
            onClose={handleCloseModal}
            fullScreen={fullScreen}
            maxWidth="md"
            fullWidth={true}
            PaperProps={{
                sx: {
                    borderRadius: fullScreen ? 0 : 1,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: 1, borderColor: 'divider',
                bgcolor: 'surface.level1',
                py: 2
            }}>
                <Typography variant="h6" fontWeight={600} color="text.primary">
                    {activity?.id ? 'Edit Activity' : 'Create Activity'}
                </Typography>
                <IconButton onClick={handleCloseModal} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                    {/* Artist Selection */}
                    {artists.length > 1 && currentActivity && !currentActivity.artistId && (
                        <FormControl fullWidth>
                            <InputLabel>Artist</InputLabel>
                            <Select
                                value={currentActivity.artistId || ""}
                                onChange={(e) => setProperty("artistId", e.target.value)}
                                label="Artist"
                            >
                                {artists.map((artist) => (
                                    <MenuItem key={artist.id} value={artist.id}>{artist.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Event Selection */}
                    {!event && !currentActivity.eventId && (
                        <FormControl fullWidth>
                            <InputLabel>Event</InputLabel>
                            <Select
                                value={currentActivity.eventId || ""}
                                onChange={(e) => {
                                    const selectedEventId = e.target.value;
                                    setProperty("eventId", selectedEventId);
                                    const selected = events.find(ev => ev.id === selectedEventId);
                                    if (selected?.date) {
                                        setProperty("startTimeTs", new Date(selected.date));
                                        if (selected.timezone) {
                                            setProperty("startTimeZone", selected.timezone);
                                            setProperty("endTimeZone", selected.timezone);
                                        }
                                    }
                                }}
                                label="Event"
                            >
                                <MenuItem value=""><em>Select an event...</em></MenuItem>
                                {events && events.length > 0 ? (
                                    events.map(ev => (
                                        <MenuItem key={ev.id} value={ev.id}>
                                            {ev.name || moment(ev.date).format('YYYY-MM-DD')}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem value="" disabled>No events found</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    )}

                    {/* Activity Type */}
                    <Autocomplete
                        options={ActivityTypes}
                        groupBy={(option) => option.category || 'Other'}
                        getOptionLabel={(option) => option.name}
                        value={ActivityTypes.find(t => t.value === currentActivity.type) || null}
                        onChange={(event, newValue) => setProperty("type", newValue ? newValue.value : "")}
                        renderInput={(params) => <TextField {...params} label="Type" fullWidth />}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {option.icon} {option.name}
                            </Box>
                        )}
                        disableClearable
                    />

                    {/* Booking / Task Settings */}
                    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={currentActivity.needsBooking || false}
                                    onChange={(e) => setProperty("needsBooking", e.target.checked)}
                                />
                            }
                            label="Needs Booking (Task)"
                        />
                        {currentActivity.needsBooking && (
                            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Assigned To</InputLabel>
                                    <Select
                                        value={currentActivity.bookingAssignedToId || ""}
                                        onChange={(e) => setProperty("bookingAssignedToId", e.target.value)}
                                        label="Assigned To"
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {availableCrewMembers.map(cm => (
                                            <MenuItem key={cm.user?.id} value={cm.user?.id}>
                                                {cm.user?.firstName} {cm.user?.lastName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={currentActivity.bookingStatus || "PENDING"}
                                        onChange={(e) => setProperty("bookingStatus", e.target.value)}
                                        label="Status"
                                    >
                                        <MenuItem value="PENDING">Pending</MenuItem>
                                        <MenuItem value="BOOKED">Booked</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        )}
                    </Box>

                    {/* Flight Number and Bookings - shown right under type when Flight is selected */}
                    {currentActivity.type === 'FLIGHT' && (
                        <>
                            <TextField
                                fullWidth
                                label="Flight Number"
                                placeholder="e.g. SK455 or SK455, SK1462"
                                helperText="Skriv ett eller flere flightnr (f.eks. SK455, SK1462) og trykk søk for å hente tider, flyplasser og mellomlandinger"
                                value={currentActivity.flightNumber || ""}
                                onChange={(e) => setProperty("flightNumber", e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleFlightSearch();
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handleFlightSearch}
                                                disabled={searchingFlight}
                                                edge="end"
                                                color="primary"
                                            >
                                                {searchingFlight ? <CircularProgress size={24} color="inherit" /> : <SearchIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Flight Booking References */}
                            <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                                    Booking References & Seats
                                </Typography>
                                <Stack spacing={2}>
                                    {bookings.map((booking, index) => (
                                        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, bgcolor: 'surface.level1', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                <TextField
                                                    size="small"
                                                    label="Booking Number (PNR)"
                                                    value={booking.reference}
                                                    onChange={(e) => {
                                                        const newBookings = [...bookings];
                                                        newBookings[index].reference = e.target.value;
                                                        setBookings(newBookings);
                                                    }}
                                                    sx={{ flexGrow: 1 }}
                                                />
                                                <IconButton
                                                    color="error"
                                                    onClick={() => setBookings(bookings.filter((_, i) => i !== index))}
                                                    size="small"
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <Autocomplete
                                                multiple
                                                size="small"
                                                options={crewPickerOptions}
                                                filterOptions={filterCrewOptions}
                                                getOptionLabel={(option) => {
                                                    const u = option.user;
                                                    return u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() : option.name || "Unknown Crew";
                                                }}
                                                renderOption={(props, option) => {
                                                    const name = `${option.user?.firstName || ""} ${option.user?.lastName || ""}`.trim();
                                                    return <li {...props} key={option.user?.id}>{option.inEvent ? name : `+ Add ${name} to event`}</li>;
                                                }}
                                                value={crewPickerOptions.filter(cm => booking.crewIds.includes(cm.user?.id))}
                                                isOptionEqualToValue={(option, value) => option?.user?.id === value?.user?.id}
                                                onChange={(event, newValue) => {
                                                    const newBookingCrewIds = newValue.map(v => v.user?.id).filter(id => !!id);
                                                    const newBookings = [...bookings];
                                                    newBookings[index].crewIds = newBookingCrewIds;
                                                    setBookings(newBookings);
                                                    // Remove from manual Select Users state so they aren't duplicated in UI
                                                    if (newBookingCrewIds.length > 0) {
                                                        const addedSet = new Set(newBookingCrewIds);
                                                        setSelectedCrewIds(prev => prev.filter(id => !addedSet.has(id)));
                                                    }
                                                }}
                                                renderTags={(value, getTagProps) =>
                                                    value.map((option, tagIndex) => (
                                                        <Chip
                                                            label={option.user ? `${option.user.firstName} ${option.user.lastName}`.trim() : option.name}
                                                            size="small"
                                                            {...getTagProps({ index: tagIndex })}
                                                            sx={{ borderRadius: '8px' }}
                                                        />
                                                    ))
                                                }
                                                renderInput={(params) => (
                                                    <TextField {...params} placeholder="Select users for this booking..." />
                                                )}
                                            />
                                        </Box>
                                    ))}
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={() => setBookings([...bookings, { id: null, reference: "", crewIds: [], existingSeats: [] }])}
                                        size="small"
                                        variant="outlined"
                                        sx={{ alignSelf: 'flex-start', borderRadius: 2 }}
                                    >
                                        Add Booking Reference
                                    </Button>
                                </Stack>
                            </Box>
                        </>
                    )}

                    {/* Activity Status */}
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={currentActivity.status || "CONFIRMED"}
                            onChange={(e) => setProperty("status", e.target.value)}
                            label="Status"
                        >
                            {ActivityStatuses.map((status) => (
                                <MenuItem key={status.value} value={status.value} sx={{ gap: 1 }}>
                                    {status.icon} {status.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Vehicle Selection (For Transport/Travel) */}
                    {['TRANSPORT', 'NIGHTLINER', 'CAR', 'BUS', 'BUS_CALL'].includes(currentActivity.type) && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <FormControl fullWidth>
                                <InputLabel>Vehicle</InputLabel>
                                <Select
                                    value={currentActivity.vehicleId || ""}
                                    onChange={(e) => {
                                        if (e.target.value === "CREATE_NEW") {
                                            setIsCreatingVehicle(true);
                                        } else {
                                            setProperty("vehicleId", e.target.value);
                                        }
                                    }}
                                    label="Vehicle"
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {vehicles.map(v => (
                                        <MenuItem key={v.id} value={v.id}>
                                            {v.name} {v.registrationNumber ? `(${v.registrationNumber})` : ''}
                                        </MenuItem>
                                    ))}
                                    <Divider />
                                    <MenuItem value="CREATE_NEW" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                        <AddIcon fontSize="small" sx={{ mr: 1 }} /> Create New Vehicle
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    {/* Driving Time (Specific for Nightliner/Transport) */}
                    {['NIGHTLINER', 'DRIVE', 'BUS', 'VAN', 'TRANSPORT', 'CAR', 'TRANSFER'].includes(currentActivity.type) && (
                        <TextField
                            fullWidth
                            label="Estimated Driving Time (minutes)"
                            type="number"
                            value={currentActivity.drivingTime || ""}
                            onChange={(e) => setProperty("drivingTime", e.target.value)}
                            helperText="Used for calculating compliance and summarizing travel time."
                        />
                    )}

                    {/* Name & Description */}
                    {currentActivity.type !== 'FLIGHT' && (
                        <TextField
                            fullWidth
                            label="Activity Name"
                            placeholder="e.g. Soundcheck, Dinner"
                            value={currentActivity.name || ""}
                            onChange={(e) => setProperty("name", e.target.value)}
                        />
                    )}

                    <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={3}
                        value={currentActivity.description || ""}
                        onChange={(e) => setProperty("description", e.target.value)}
                    />

                    {/* Participants Selection - PROMINENT PLACE */}
                    <Box sx={{ mt: 1, mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                            Assign Crew & Groups
                        </Typography>
                        <Stack spacing={2}>
                            <Autocomplete
                                multiple
                                options={availableGroups.filter(g => {
                                    return availableCrewMembers.some(cm => cm.inEvent && cm.group?.id === g.id);
                                })}
                                getOptionLabel={(option) => {
                                    const count = availableCrewMembers.filter(cm => cm.inEvent && cm.group?.id === option.id).length;
                                    return `${option.name || ""} (${count})`;
                                }}
                                value={availableGroups.filter(g => selectedGroupIds.includes(g.id))}
                                isOptionEqualToValue={(option, value) => {
                                    if (!option?.id || !value?.id) return false;
                                    return option.id === value.id;
                                }}
                                onChange={(event, newValue) => {
                                    setSelectedGroupIds(newValue.map(v => v.id));
                                }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const count = availableCrewMembers.filter(cm => cm.inEvent && cm.group?.id === option.id).length;
                                        return (
                                            <Chip
                                                label={`${option.name} (${count})`}
                                                size="small"
                                                {...getTagProps({ index })}
                                                sx={{
                                                    bgcolor: '#007fff',
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    borderRadius: '8px',
                                                    '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }
                                                }}
                                            />
                                        );
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Groups"
                                        placeholder="Search groups..."
                                        size="small"
                                    />
                                )}
                            />

                            <Autocomplete
                                multiple
                                options={crewPickerOptions.filter(c => !c.user?.id || !bookingCrewIdsSet.has(c.user.id))}
                                filterOptions={filterCrewOptions}
                                getOptionLabel={(option) => {
                                    if (!option.user) return "";
                                    const { firstName, lastName, email, phone } = option.user;
                                    const cleanFirst = (firstName && firstName !== 'null') ? firstName : "";
                                    const cleanLast = (lastName && lastName !== 'null') ? lastName : "";
                                    const fullName = `${cleanFirst} ${cleanLast}`.trim();
                                    return fullName || email || phone || "Unknown Member";
                                }}
                                value={crewPickerOptions.filter(c => c.user?.id && selectedCrewIds.includes(c.user.id) && !bookingCrewIdsSet.has(c.user.id))}
                                isOptionEqualToValue={(option, value) => {
                                    if (!option?.user?.id || !value?.user?.id) return false;
                                    return option.user.id === value.user.id;
                                }}
                                onChange={(event, newValue) => {
                                    const ids = newValue.map(v => v.user?.id).filter(id => !!id);
                                    setSelectedCrewIds([...new Set(ids)]);
                                }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option.user?.id || option.id || index}
                                            avatar={<Avatar sx={{ width: 20, height: 20, fontSize: '0.7rem', bgcolor: 'primary.dark' }}>{(option.user?.firstName || option.user?.email || "U")[0]}</Avatar>}
                                            label={(() => {
                                                const { firstName, lastName, email, phone } = option.user || {};
                                                const cleanFirst = (firstName && firstName !== 'null') ? firstName : "";
                                                const cleanLast = (lastName && lastName !== 'null') ? lastName : "";
                                                const fullName = `${cleanFirst} ${cleanLast}`.trim();
                                                return fullName || email || phone || "Unknown Member";
                                            })()}
                                            size="small"
                                            sx={{
                                                bgcolor: '#007fff',
                                                color: 'white',
                                                fontWeight: 600,
                                                borderRadius: '8px',
                                                '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }
                                            }}
                                        />
                                    ))
                                }
                                renderOption={(props, option) => (
                                    <li {...props} key={option.user?.id || option.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>
                                                {(option.user?.firstName || option.user?.email || "U")[0]}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2">
                                                    {(() => {
                                                        const { firstName, lastName, email, phone } = option.user || {};
                                                        const cleanFirst = (firstName && firstName !== 'null') ? firstName : "";
                                                        const cleanLast = (lastName && lastName !== 'null') ? lastName : "";
                                                        const fullName = `${cleanFirst} ${cleanLast}`.trim() || email || phone || "Unknown";
                                                        return option.inEvent ? fullName : `+ Add ${fullName} to event`;
                                                    })()}
                                                </Typography>
                                                {option.user?.email && (
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2, fontSize: '0.7rem' }}>
                                                        {option.user.email}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Users"
                                        placeholder="Search users..."
                                        size="small"
                                    />
                                )}
                            />

                            {/* Affected Users Summary */}
                            <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                    {(selectedGroupIds.length === 0 && selectedCrewIds.length === 0 && bookingCrewIdsSet.size === 0)
                                        ? `Affects everyone in this event (${affectedUsers.length} people)`
                                        : `Affects ${affectedUsers.length} specifically selected people`}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
                                    {affectedUsers.length > 0
                                        ? affectedUsers.map(u => `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Unknown").join(', ')
                                        : "No users affected."}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    {/* Start Time Row */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <TextField
                            fullWidth
                            label="Start Time"
                            type="datetime-local"
                            value={currentActivity.startTimeTs ? moment.tz(currentActivity.startTimeTs, currentActivity.startTimeZone).format('YYYY-MM-DDTHH:mm') : ""}
                            onChange={(e) => setProperty("startTimeTs", moment.tz(e.target.value, currentActivity.startTimeZone).toDate())}
                            InputLabelProps={{ shrink: true }}
                        />
                        <Autocomplete
                            options={typeof Intl.supportedValuesOf === "function"
                                ? Intl.supportedValuesOf("timeZone")
                                : ["UTC", "Europe/Oslo", "America/New_York"]
                            }
                            value={currentActivity.startTimeZone || ""}
                            onChange={(event, newValue) => {
                                handleTimezoneChange("startTimeZone", newValue);
                            }}
                            renderInput={(params) => <TextField {...params} label="Time Zone" size="small" />}
                            sx={{ minWidth: 200 }}
                        />
                    </Box>

                    {/* End Time Row */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <TextField
                            fullWidth
                            label="End Time"
                            type="datetime-local"
                            value={currentActivity.endTimeTs ? moment.tz(currentActivity.endTimeTs, currentActivity.endTimeZone || currentActivity.startTimeZone).format('YYYY-MM-DDTHH:mm') : ""}
                            onChange={(e) => setProperty("endTimeTs", e.target.value ? moment.tz(e.target.value, currentActivity.endTimeZone || currentActivity.startTimeZone).toDate() : null)}
                            onClick={() => {
                                if (!currentActivity.endTimeTs && currentActivity.startTimeTs) {
                                    const newEndTime = moment(currentActivity.startTimeTs).add(30, 'minutes').toDate();
                                    setProperty("endTimeTs", newEndTime);
                                }
                            }}
                            InputLabelProps={{ shrink: true }}
                        />
                        <Autocomplete
                            options={typeof Intl.supportedValuesOf === "function"
                                ? Intl.supportedValuesOf("timeZone")
                                : ["UTC", "Europe/Oslo", "America/New_York"]
                            }
                            value={currentActivity.endTimeZone || currentActivity.startTimeZone || ""}
                            onChange={(event, newValue) => {
                                handleTimezoneChange("endTimeZone", newValue);
                            }}
                            renderInput={(params) => <TextField {...params} label="Time Zone" size="small" />}
                            sx={{ minWidth: 200 }}
                        />
                    </Box>

                    {/* Locations */}
                    <Box>
                        <FormControl fullWidth>
                            <InputLabel>Location (Start)</InputLabel>
                            <Select
                                value={currentActivity.locationId || ""}
                                label="Location (Start)"
                                onChange={(e) => {
                                    if (e.target.value === "CREATE_NEW") {
                                        setIsCreatingStart(true);
                                        setProperty("locationId", "");
                                    } else {
                                        const loc = availableLocations.find(l => l.id === e.target.value);
                                        setProperty("locationId", e.target.value);
                                        setProperty("location", loc);
                                    }
                                }}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {availableLocations.map((l) => (
                                    <MenuItem key={l.id} value={l.id}>
                                        <Typography variant="inherit" component="span" sx={{ fontWeight: l.context ? 600 : 400 }}>
                                            {l.name}
                                        </Typography>
                                        {l.context && (
                                            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                                {l.context}
                                            </Typography>
                                        )}
                                        {!l.context && l.city && ` (${l.city})`}
                                    </MenuItem>
                                ))}
                                {availableLocations.length > 0 && <Divider />}
                                <MenuItem value="CREATE_NEW" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    <ListItemIcon>
                                        <AddIcon fontSize="small" color="primary" />
                                    </ListItemIcon>
                                    Create New Location
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Stops Editor (v2) for eligible types */}
                    {STOPS_ELIGIBLE_TYPES.includes(currentActivity.type) && (
                        <Box sx={{ ml: 2, mr: 2 }}>
                            <StopsEditor
                                activity={currentActivity}
                                stops={activityStops}
                                legs={activityLegs}
                                onStopsChange={(newStops, newLegs) => {
                                    setActivityStops(newStops);
                                    setActivityLegs(newLegs);
                                }}
                                availableCrewMembers={availableCrewMembers}
                                isFlightType={currentActivity.type === 'FLIGHT'}
                                startLocation={currentActivity.location}
                                endLocation={currentActivity.endLocation}
                                startTime={currentActivity.startTimeTs}
                                startTimeZone={currentActivity.startTimeZone}
                                hideEndpoints={true}
                            />
                        </Box>
                    )}

                    {/* End Location */}
                    {['TRANSPORT', 'NIGHTLINER', 'CAR', 'BUS', 'TAXI', 'FLIGHT', 'FERRY', 'WALK'].includes(currentActivity.type) && (
                        <Box>
                            <FormControl fullWidth>
                                <InputLabel>Location (End)</InputLabel>
                                <Select
                                    value={currentActivity.endLocationId || ""}
                                    label="Location (End)"
                                    onChange={(e) => {
                                        if (e.target.value === "CREATE_NEW") {
                                            setIsCreatingEnd(true);
                                            setProperty("endLocationId", "");
                                        } else {
                                            const loc = availableLocations.find(l => l.id === e.target.value);
                                            setProperty("endLocationId", e.target.value);
                                            setProperty("endLocation", loc);
                                        }
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {availableLocations.map((l) => (
                                        <MenuItem key={l.id} value={l.id}>
                                            <Typography variant="inherit" component="span" sx={{ fontWeight: l.context ? 600 : 400 }}>
                                                {l.name}
                                            </Typography>
                                            {l.context && (
                                                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                                    {l.context}
                                                </Typography>
                                            )}
                                            {!l.context && l.city && ` (${l.city})`}
                                        </MenuItem>
                                    ))}
                                    {availableLocations.length > 0 && <Divider />}
                                    <MenuItem value="CREATE_NEW" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                        <ListItemIcon>
                                            <AddIcon fontSize="small" color="primary" />
                                        </ListItemIcon>
                                        Create New Location
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    {/* Route Calculation */}
                    {currentActivity.locationId && currentActivity.endLocationId && currentActivity.type !== 'FLIGHT' && (
                        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Route Calculator</Typography>

                            {/* Calculation Settings */}
                            {['NIGHTLINER', 'BUS', 'BUS_CALL'].includes(currentActivity.type) && (
                                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <TextField
                                            label="Max Speed (km/h)"
                                            type="number"
                                            size="small"
                                            value={calcSettings.speed}
                                            onChange={(e) => setCalcSettings({ ...calcSettings, speed: Number(e.target.value) })}
                                            sx={{ width: 140 }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={calcSettings.enableBusRules}
                                                    onChange={(e) => setCalcSettings({ ...calcSettings, enableBusRules: e.target.checked })}
                                                    size="small"
                                                />
                                            }
                                            label={<Typography variant="body2">Bus Regulations</Typography>}
                                        />
                                    </Box>

                                    {calcSettings.enableBusRules && (
                                        <FormControl component="fieldset">
                                            <FormLabel component="legend" sx={{ fontSize: '0.75rem' }}>Driver Setup</FormLabel>
                                            <RadioGroup
                                                row
                                                value={calcSettings.driverType}
                                                onChange={(e) => setCalcSettings({ ...calcSettings, driverType: e.target.value })}
                                            >
                                                <FormControlLabel value="SINGLE" control={<Radio size="small" />} label={<Typography variant="body2">Single Driver</Typography>} />
                                                <FormControlLabel value="DOUBLE" control={<Radio size="small" />} label={<Typography variant="body2">Double Driver</Typography>} />
                                            </RadioGroup>
                                        </FormControl>
                                    )}
                                </Box>
                            )}

                            {/* Legacy Waypoints for non-eligible types */}
                            {!STOPS_ELIGIBLE_TYPES.includes(currentActivity.type) && (
                            <Box sx={{ mb: 2, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>Waypoints (Stops)</Typography>
                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                    {waypoints.map((wp, idx) => (
                                        <Chip
                                            key={idx}
                                            label={wp.name || wp.formattedAddress || `Stop ${idx + 1}`}
                                            onDelete={() => {
                                                const newWps = [...waypoints];
                                                newWps.splice(idx, 1);
                                                setWaypoints(newWps);
                                            }}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Stack>
                                <LocationSearchField
                                    key={waypoints.length}
                                    label="Add Stop/Waypoint..."
                                    onChange={(loc) => {
                                        if (loc) {
                                            setWaypoints([...waypoints, loc]);
                                        }
                                    }}
                                />
                            </Box>
                            )}

                            <Button
                                variant="outlined"
                                onClick={handleCalculateRoute}
                                fullWidth
                                disabled={calculating}
                                sx={{ mb: 2 }}
                            >
                                {calculating ? "Calculating..." : "Calculate Route"}
                            </Button>

                            {/* Route Route Options */}
                            {directionsResult && (
                                <Box sx={{ mb: 2, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                    <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>Select Route Option</Typography>
                                    <RadioGroup
                                        value={routeIndex}
                                        onChange={(e) => {
                                            const idx = parseInt(e.target.value);
                                            setRouteIndex(idx);
                                            updateActivityWithRoute(directionsResult, idx);
                                        }}
                                    >
                                        {directionsResult.routes.map((route, i) => (
                                            <FormControlLabel
                                                key={i}
                                                value={i}
                                                control={<Radio size="small" />}
                                                label={
                                                    <Typography variant="body2">
                                                        {route.summary ? `${route.summary}: ` : `Route ${i + 1}: `}
                                                        <b>{(route.legs.reduce((acc, l) => acc + l.distance.value, 0) / 1000).toFixed(1)} km</b> /
                                                        {Math.floor(route.legs.reduce((acc, l) => acc + l.duration.value, 0) / 60)} min
                                                    </Typography>
                                                }
                                            />
                                        ))}
                                    </RadioGroup>
                                </Box>
                            )}

                            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Vehicle</InputLabel>
                                    <Select
                                        value={currentActivity.vehicleId || ""}
                                        label="Vehicle"
                                        onChange={(e) => {
                                            if (e.target.value === "CREATE_NEW") {
                                                setIsCreatingVehicle(true);
                                            } else {
                                                setProperty("vehicleId", e.target.value);
                                            }
                                        }}
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {vehicles.map(v => (
                                            <MenuItem key={v.id} value={v.id}>{v.name} ({v.registrationNumber})</MenuItem>
                                        ))}
                                        <Divider />
                                        <MenuItem value="CREATE_NEW" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add New Vehicle
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="Distance"
                                    value={currentActivity.distance || ""}
                                    onChange={(e) => setProperty("distance", e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                                <TextField
                                    label="Duration"
                                    value={currentActivity.duration || ""}
                                    onChange={(e) => setProperty("duration", e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                                <TextField
                                    label="Driving Time (min)"
                                    type="number"
                                    value={currentActivity.drivingTime || ""}
                                    onChange={(e) => setProperty("drivingTime", parseInt(e.target.value) || 0)}
                                    fullWidth
                                    size="small"
                                    helperText="Total driving minutes"
                                />
                            </Box>


                            {/* Driver Selection */}
                            <Box sx={{ mt: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>Driver Allocation</Typography>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        value={areDriversAllocated ? (isDoubleDriver ? "double" : "single") : "none"}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === "none") {
                                                setAreDriversAllocated(false);
                                                setIsDoubleDriver(false);
                                                setProperty("mainDriverId", null);
                                                setProperty("doubleDriverId", null);
                                            } else if (val === "single") {
                                                setAreDriversAllocated(true);
                                                setIsDoubleDriver(false);
                                                setProperty("doubleDriverId", null);
                                            } else {
                                                setAreDriversAllocated(true);
                                                setIsDoubleDriver(true);
                                            }
                                        }}
                                    >
                                        <FormControlLabel value="none" control={<Radio />} label="No specific driver" />
                                        <FormControlLabel value="single" control={<Radio />} label="Single Driver" />
                                        <FormControlLabel value="double" control={<Radio />} label="Double Driver" />
                                    </RadioGroup>
                                </FormControl>

                                {areDriversAllocated && (
                                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Main Driver</InputLabel>
                                            <Select
                                                value={currentActivity.mainDriverId || ""}
                                                label="Main Driver"
                                                onChange={(e) => setProperty("mainDriverId", e.target.value)}
                                            >
                                                <MenuItem value=""><em>Select Driver...</em></MenuItem>
                                                {availableCrewMembers.map(c => (
                                                    <MenuItem key={c.user.id} value={c.user.id}>
                                                        {c.user.firstName} {c.user.lastName} ({c.role})
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {isDoubleDriver && (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Second Driver</InputLabel>
                                                <Select
                                                    value={currentActivity.doubleDriverId || ""}
                                                    label="Second Driver"
                                                    onChange={(e) => setProperty("doubleDriverId", e.target.value)}
                                                >
                                                    <MenuItem value=""><em>Select Driver...</em></MenuItem>
                                                    {availableCrewMembers.map(c => (
                                                        <MenuItem key={c.user.id} value={c.user.id}>
                                                            {c.user.firstName} {c.user.lastName} ({c.role})
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </Box>
                                )}

                                {areDriversAllocated && currentActivity.drivingTime > 0 && (
                                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                                        Estimated driving time per driver: {isDoubleDriver ? Math.round(currentActivity.drivingTime / 2) : currentActivity.drivingTime} min
                                    </Typography>
                                )}
                            </Box>
                            {currentActivity.routeDetails && (
                                <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', color: 'text.primary', borderRadius: 1, border: '1px solid #bbdefb' }}>
                                    {(() => {
                                        try {
                                            const details = JSON.parse(currentActivity.routeDetails);
                                            if (!details || details.length === 0) return null;

                                            return (
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">
                                                        Itinerary Preview
                                                    </Typography>
                                                    <Stack spacing={1}>
                                                        {details.map((step, idx) => {
                                                            const startTime = currentActivity.startTimeTs ? moment(currentActivity.startTimeTs).add(step.startTimeOffset || 0, 'seconds') : null;
                                                            const durationMin = Math.round((step.duration || 0) / 60);

                                                            return (
                                                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem' }}>
                                                                    <Box sx={{ width: 60, fontWeight: 'bold', color: 'text.secondary', textAlign: 'right' }}>
                                                                        {startTime ? startTime.format('HH:mm') : `+${Math.floor((step.startTimeOffset || 0) / 3600)}h ${Math.floor(((step.startTimeOffset || 0) % 3600) / 60)}m`}
                                                                    </Box>
                                                                    <Chip
                                                                        label={step.type === 'FERRY' ? 'Ferry' : 'Drive'}
                                                                        size="small"
                                                                        color={step.type === 'FERRY' ? 'warning' : 'default'}
                                                                        variant="outlined"
                                                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                                                    />
                                                                    <Typography variant="caption" noWrap sx={{ flex: 1, maxWidth: 300 }}>
                                                                        {step.description || (step.type === "FERRY" ? "Ferry Crossing" : "Driving Segment")}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {durationMin} min
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Stack>
                                                </Box>
                                            );
                                        } catch (e) { return null; }
                                    })()}
                                </Box>
                            )}

                            {/* Map Preview */}
                            {/* Map Preview */}
                            {(isLoaded && window.google) && (
                                <Box sx={{ mt: 2, height: 300, width: '100%', borderRadius: 1, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                                    <GoogleMap
                                        key={`${currentActivity.locationId}-${currentActivity.endLocationId}`}
                                        mapContainerStyle={{ height: '100%', width: '100%' }}
                                        center={{ lat: currentActivity.location?.latitude || 0, lng: currentActivity.location?.longitude || 0 }}
                                        zoom={8}
                                        options={{
                                            streetViewControl: false,
                                            mapTypeControl: false,
                                            fullscreenControl: false
                                        }}
                                        onLoad={(map) => {
                                            const bounds = new window.google.maps.LatLngBounds();
                                            if (currentActivity.location?.latitude) bounds.extend({ lat: currentActivity.location.latitude, lng: currentActivity.location.longitude });
                                            if (currentActivity.endLocation?.latitude) bounds.extend({ lat: currentActivity.endLocation.latitude, lng: currentActivity.endLocation.longitude });

                                            // Also extend bounds to include the route path if available!
                                            if (decodedPath) {
                                                decodedPath.forEach(pt => bounds.extend(pt));
                                            }

                                            map.fitBounds(bounds);
                                        }}
                                    >
                                        {currentActivity.location?.latitude && (
                                            <Marker position={{ lat: currentActivity.location.latitude, lng: currentActivity.location.longitude }} label="A" />
                                        )}
                                        {currentActivity.endLocation?.latitude && (
                                            <Marker position={{ lat: currentActivity.endLocation.latitude, lng: currentActivity.endLocation.longitude }} label="B" />
                                        )}

                                        {/* Show DirectionsRenderer if we have a calculated result */}
                                        {directionsResult ? (
                                            <DirectionsRenderer
                                                directions={directionsResult}
                                                routeIndex={routeIndex}
                                                options={{
                                                    suppressMarkers: false,
                                                    preserveViewport: true, // We handle bounds manually
                                                    polylineOptions: { strokeColor: "#2196F3", strokeWeight: 5 }
                                                }}
                                            />
                                        ) : (
                                            /* Fallback: Saved polyline from DB */
                                            decodedPath ? (
                                                <Polyline
                                                    path={decodedPath}
                                                    options={{ strokeColor: "#2196F3", strokeWeight: 4 }}
                                                />
                                            ) : (
                                                /* Fallback: Straight line */
                                                (currentActivity.location?.latitude && currentActivity.endLocation?.latitude) && (
                                                    <Polyline
                                                        path={[
                                                            { lat: currentActivity.location.latitude, lng: currentActivity.location.longitude },
                                                            { lat: currentActivity.endLocation.latitude, lng: currentActivity.endLocation.longitude }
                                                        ]}
                                                        options={{ strokeColor: "#9e9e9e", strokeOpacity: 0.5, strokeWeight: 2, geodesic: true }}

                                                    />
                                                )
                                            )
                                        )}
                                    </GoogleMap>
                                </Box>
                            )}


                        </Box>
                    )}

                    {/* Create Vehicle Dialog */}
                    <Dialog open={isCreatingVehicle} onClose={() => setIsCreatingVehicle(false)}>
                        <DialogTitle>Add New Vehicle</DialogTitle>
                        <DialogContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 300 }}>
                                <TextField
                                    label="Vehicle Name (e.g. Nightliner 1)"
                                    fullWidth
                                    value={newVehicle.name}
                                    onChange={(e) => setNewVehicle({ ...newVehicle, name: e.target.value })}
                                />
                                <TextField
                                    label="Registration Number"
                                    fullWidth
                                    value={newVehicle.registrationNumber}
                                    onChange={(e) => setNewVehicle({ ...newVehicle, registrationNumber: e.target.value })}
                                />
                                <TextField
                                    label="Capacity"
                                    type="number"
                                    fullWidth
                                    value={newVehicle.capacity}
                                    onChange={(e) => setNewVehicle({ ...newVehicle, capacity: parseInt(e.target.value) || 0 })}
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setIsCreatingVehicle(false)}>Cancel</Button>
                            <Button variant="contained" onClick={async () => {
                                if (!newVehicle.name) return;
                                try {
                                    const { data } = await createVehicle({
                                        artistId: currentActivity.artistId,
                                        name: newVehicle.name,
                                        registrationNumber: newVehicle.registrationNumber,
                                        capacity: newVehicle.capacity,
                                        type: "BUS"
                                    });
                                    // Refresh vehicles and select the new one
                                    await fetchVehicles(currentActivity.artistId);
                                    setProperty("vehicleId", data.vehicle_insert.id);
                                    setIsCreatingVehicle(false);
                                    setNewVehicle({ name: "", registrationNumber: "", capacity: 0 });
                                } catch (e) {
                                    console.error("Error creating vehicle", e);
                                    alert("Could not create vehicle");
                                }
                            }}>Create</Button>
                        </DialogActions>
                    </Dialog>

                    <ActivityFileList
                        activityId={currentActivity?.id}
                        currentActivity={currentActivity}
                        setCurrentActivity={setCurrentActivity}
                    />

                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: 'surface.level1', borderTop: 1, borderColor: 'divider' }}>
                {currentActivity?.id && hasDeletePermission && (
                    <Button onClick={handleDelete} color="error" sx={{ mr: 'auto' }}>
                        Delete
                    </Button>
                )}
                <Button onClick={handleCloseModal} color="inherit" disabled={isSaving}>
                    Cancel
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1, mr: 1, flexWrap: 'wrap' }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={notifyUsers}
                                onChange={(e) => setNotifyUsers(e.target.checked)}
                                color="primary"
                                size="small"
                            />
                        }
                        label={<Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Notify Team</Typography>}
                        sx={{ m: 0 }}
                    />
                    {notifyUsers && (
                        <TextField
                            size="small"
                            placeholder="Optional message..."
                            value={notifyMessage}
                            onChange={(e) => setNotifyMessage(e.target.value)}
                            sx={{ minWidth: 150, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8rem' } }}
                        />
                    )}
                </Box>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{
                        borderRadius: 1,
                        textTransform: 'none',
                        bgcolor: '#238636',
                        '&:hover': { bgcolor: '#2ea043' },
                        minHeight: fullScreen ? 48 : 40,
                        flex: fullScreen ? 1 : 'none',
                        maxWidth: fullScreen ? 'calc(50% - 4px)' : 'auto'
                    }}
                >
                    {isSaving ? "Saving..." : (currentActivity?.id ? "Update" : "Create")}
                </Button>
            </DialogActions>

            {/* Add New Location Modal (Start) */}
            <Dialog open={isCreatingStart} onClose={() => setIsCreatingStart(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add New Location</DialogTitle>
                <DialogContent>
                    <LocationSearchField
                        location={{ name: currentActivity.location?.name || "" }}
                        onChange={(data) => {
                            if (data.id) {
                                setProperty("locationId", data.id);
                                setProperty("location", data);
                                setIsCreatingStart(false);
                            }
                        }}
                        artistId={currentActivity.artistId}
                        fieldKey="start_loc_search"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsCreatingStart(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Add New Location Modal (End) */}
            <Dialog open={isCreatingEnd} onClose={() => setIsCreatingEnd(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add New Location</DialogTitle>
                <DialogContent>
                    <LocationSearchField
                        location={{ name: currentActivity.endLocation?.name || "" }}
                        onChange={(data) => {
                            if (data.id) {
                                setProperty("endLocationId", data.id);
                                setProperty("endLocation", data);
                                setIsCreatingEnd(false);
                            }
                        }}
                        artistId={currentActivity.artistId}
                        fieldKey="end_loc_search"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsCreatingEnd(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <TmDialog
                open={showDeleteConfirm}
                title="Delete Activity?"
                text={`Are you sure you want to delete "${currentActivity.name || 'this activity'}"? This action cannot be undone.`}
                answer1="Cancel"
                answer2="Delete"
                onClose={() => setShowDeleteConfirm(false)}
                onClickAnswer1={() => setShowDeleteConfirm(false)}
                onClickAnswer2={executeDelete}
            />
        </Dialog >
    );
};

export default ActivityModal;
